import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 20, right: 30, bottom: 50, left: 60 };
const TOP_CHART_HEIGHT = 260;
const BOTTOM_CHART_HEIGHT = 180;
const CHART_GAP = 30;
const SVG_HEIGHT = TOP_CHART_HEIGHT + BOTTOM_CHART_HEIGHT + CHART_GAP;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
  },
  headerText: {
    flex: 1,
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    margin: 0,
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    margin: '4px 0 0 0',
  },
  statsRow: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
    flexShrink: 0,
    marginLeft: '16px',
  },
  statBox: {
    textAlign: 'center',
    padding: '6px 12px',
    background: '#f9fafb',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
  },
  statLabel: {
    fontSize: '10px',
    color: '#9ca3af',
    marginBottom: '2px',
  },
  statValue: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#1f2937',
  },
  badge: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  legend: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    marginBottom: '8px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    color: '#6b7280',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

const SIGNAL_COLOR = '#2563eb';
const OBSERVED_COLOR = '#6b7280';
const NOISE_COLOR = '#f59e0b';
const SNR_LINE_COLOR = '#8b5cf6';

const STRENGTH_CONFIG = {
  strong: { label: 'Strong Signal', background: '#dcfce7', color: '#166534', border: '#bbf7d0' },
  moderate: { label: 'Moderate Signal', background: '#fef9c3', color: '#854d0e', border: '#fde68a' },
  weak: { label: 'Weak Signal', background: '#fee2e2', color: '#991b1b', border: '#fecaca' },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

export default function PriceSignalNoiseChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const chartData = useMemo(() => {
    if (!data || !data.dates || data.dates.length === 0) return null;
    const n = data.dates.length;
    const parsed = [];
    for (let i = 0; i < n; i++) {
      parsed.push({
        date: new Date(data.dates[i]),
        dateStr: data.dates[i],
        observed: data.observed ? data.observed[i] : null,
        signal: data.signal ? data.signal[i] : null,
        noise: data.noise ? data.noise[i] : null,
        snr: data.snr ? data.snr[i] : null,
      });
    }
    return parsed;
  }, [data]);

  useEffect(() => {
    if (!chartData || chartData.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(700, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;

    svg
      .attr('viewBox', `0 0 ${width} ${SVG_HEIGHT}`)
      .attr('width', '100%')
      .attr('height', SVG_HEIGHT)
      .style('overflow', 'visible');

    const n = chartData.length;
    const parsedDates = chartData.map((d) => d.date);

    // Shared x scale
    const x = d3.scaleTime()
      .domain(d3.extent(parsedDates))
      .range([0, innerW]);

    // ── TOP CHART: Observed + Signal + Noise bars ──
    const topInnerH = TOP_CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    const allPrices = chartData
      .flatMap((d) => [d.observed, d.signal])
      .filter((v) => v != null);
    const priceMin = d3.min(allPrices);
    const priceMax = d3.max(allPrices);
    const pricePad = (priceMax - priceMin) * 0.15 || 1;

    const yTop = d3.scaleLinear()
      .domain([priceMin - pricePad, priceMax + pricePad])
      .nice()
      .range([topInnerH, 0]);

    const gTop = svg.append('g')
      .attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Grid lines
    const yTopTicks = yTop.ticks(6);
    gTop.selectAll('.grid-line')
      .data(yTopTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => yTop(d))
      .attr('y2', (d) => yTop(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Y axis
    gTop.append('g')
      .call(
        d3.axisLeft(yTop)
          .ticks(6)
          .tickSize(0)
          .tickFormat((v) => `$${v.toFixed(0)}`)
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Panel label
    gTop.append('text')
      .attr('x', 0)
      .attr('y', -6)
      .attr('font-size', '11px')
      .attr('font-weight', 600)
      .attr('fill', '#374151')
      .text('Price & Signal');

    // Noise vertical bars (from signal to observed)
    chartData.forEach((d) => {
      if (d.observed == null || d.signal == null) return;
      const cx = x(d.date);
      const y1 = yTop(d.signal);
      const y2 = yTop(d.observed);

      gTop.append('line')
        .attr('x1', cx)
        .attr('x2', cx)
        .attr('y1', y1)
        .attr('y2', y2)
        .attr('stroke', NOISE_COLOR)
        .attr('stroke-width', 2)
        .attr('stroke-opacity', 0.6);
    });

    // Signal line (smooth bold)
    const signalData = chartData.filter((d) => d.signal != null);
    if (signalData.length > 1) {
      const signalLine = d3.line()
        .x((d) => x(d.date))
        .y((d) => yTop(d.signal))
        .curve(d3.curveMonotoneX);

      gTop.append('path')
        .datum(signalData)
        .attr('d', signalLine)
        .attr('fill', 'none')
        .attr('stroke', SIGNAL_COLOR)
        .attr('stroke-width', 3);
    }

    // Observed dots
    chartData.forEach((d) => {
      if (d.observed == null) return;
      gTop.append('circle')
        .attr('cx', x(d.date))
        .attr('cy', yTop(d.observed))
        .attr('r', 3.5)
        .attr('fill', '#fff')
        .attr('stroke', OBSERVED_COLOR)
        .attr('stroke-width', 1.5);
    });

    // ── BOTTOM CHART: SNR over time with colored zones ──
    const bottomTopY = TOP_CHART_HEIGHT + CHART_GAP;
    const bottomInnerH = BOTTOM_CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    const snrValues = chartData.map((d) => d.snr).filter((v) => v != null);
    const snrMin = d3.min(snrValues) || 0;
    const snrMax = d3.max(snrValues) || 15;
    const snrPad = (snrMax - snrMin) * 0.15 || 2;

    const yBottom = d3.scaleLinear()
      .domain([Math.min(0, snrMin - snrPad), snrMax + snrPad])
      .nice()
      .range([bottomInnerH, 0]);

    const gBottom = svg.append('g')
      .attr('transform', `translate(${MARGIN.left}, ${bottomTopY})`);

    // Colored zone backgrounds
    const zoneDomain = yBottom.domain();
    const zones = [
      { min: Math.max(zoneDomain[0], 0), max: Math.min(zoneDomain[1], 5), color: '#fee2e2' },   // red (weak)
      { min: Math.max(zoneDomain[0], 5), max: Math.min(zoneDomain[1], 10), color: '#fef9c3' },   // yellow (moderate)
      { min: Math.max(zoneDomain[0], 10), max: zoneDomain[1], color: '#dcfce7' },                 // green (strong)
    ];

    zones.forEach((zone) => {
      if (zone.min >= zone.max) return;
      gBottom.append('rect')
        .attr('x', 0)
        .attr('y', yBottom(zone.max))
        .attr('width', innerW)
        .attr('height', yBottom(zone.min) - yBottom(zone.max))
        .attr('fill', zone.color)
        .attr('fill-opacity', 0.4);
    });

    // Zone threshold lines
    [5, 10].forEach((threshold) => {
      if (threshold > zoneDomain[0] && threshold < zoneDomain[1]) {
        gBottom.append('line')
          .attr('x1', 0)
          .attr('x2', innerW)
          .attr('y1', yBottom(threshold))
          .attr('y2', yBottom(threshold))
          .attr('stroke', '#d1d5db')
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '4,3');

        gBottom.append('text')
          .attr('x', innerW + 4)
          .attr('y', yBottom(threshold) + 3)
          .attr('font-size', '9px')
          .attr('fill', '#9ca3af')
          .text(threshold === 5 ? 'Weak' : 'Strong');
      }
    });

    // Grid lines
    const yBottomTicks = yBottom.ticks(5);
    gBottom.selectAll('.grid-line')
      .data(yBottomTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => yBottom(d))
      .attr('y2', (d) => yBottom(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Y axis
    gBottom.append('g')
      .call(
        d3.axisLeft(yBottom)
          .ticks(5)
          .tickSize(0)
          .tickFormat((v) => v.toFixed(0))
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // X axis (bottom chart only)
    gBottom.append('g')
      .attr('transform', `translate(0, ${bottomInnerH})`)
      .call(
        d3.axisBottom(x)
          .ticks(Math.min(n, 10))
          .tickSize(0)
          .tickFormat((d) => formatDateLabel(d.toISOString()))
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.25em');

    // Panel label
    gBottom.append('text')
      .attr('x', 0)
      .attr('y', -6)
      .attr('font-size', '11px')
      .attr('font-weight', 600)
      .attr('fill', '#374151')
      .text('Signal-to-Noise Ratio');

    // SNR line
    const snrData = chartData.filter((d) => d.snr != null);
    if (snrData.length > 1) {
      const snrLine = d3.line()
        .x((d) => x(d.date))
        .y((d) => yBottom(d.snr))
        .curve(d3.curveMonotoneX);

      gBottom.append('path')
        .datum(snrData)
        .attr('d', snrLine)
        .attr('fill', 'none')
        .attr('stroke', SNR_LINE_COLOR)
        .attr('stroke-width', 2);

      // SNR dots
      snrData.forEach((d) => {
        const snrColor = d.snr >= 10 ? '#16a34a' : d.snr >= 5 ? '#ca8a04' : '#dc2626';
        gBottom.append('circle')
          .attr('cx', x(d.date))
          .attr('cy', yBottom(d.snr))
          .attr('r', 3)
          .attr('fill', snrColor)
          .attr('stroke', '#fff')
          .attr('stroke-width', 1.5);
      });
    }

    // ── Crosshair overlay spanning both charts ──
    const crosshairGroup = svg.append('g').attr('class', 'crosshair-group');

    const crosshairTop = crosshairGroup.append('line')
      .attr('y1', MARGIN.top)
      .attr('y2', MARGIN.top + topInnerH)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    const crosshairBottom = crosshairGroup.append('line')
      .attr('y1', bottomTopY)
      .attr('y2', bottomTopY + bottomInnerH)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    const highlightObserved = crosshairGroup.append('circle')
      .attr('r', 4)
      .attr('fill', '#fff')
      .attr('stroke', OBSERVED_COLOR)
      .attr('stroke-width', 2)
      .style('display', 'none');

    const highlightSignal = crosshairGroup.append('circle')
      .attr('r', 4)
      .attr('fill', '#fff')
      .attr('stroke', SIGNAL_COLOR)
      .attr('stroke-width', 2)
      .style('display', 'none');

    const highlightSNR = crosshairGroup.append('circle')
      .attr('r', 4)
      .attr('fill', '#fff')
      .attr('stroke', SNR_LINE_COLOR)
      .attr('stroke-width', 2)
      .style('display', 'none');

    // Invisible overlay rect for mouse events
    svg.append('rect')
      .attr('x', MARGIN.left)
      .attr('y', MARGIN.top)
      .attr('width', innerW)
      .attr('height', SVG_HEIGHT - MARGIN.top)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('mousemove', (event) => {
        const [mx] = d3.pointer(event);
        const adjustedX = mx - MARGIN.left;
        const dateAtMouse = x.invert(adjustedX);

        const bisect = d3.bisector((d) => d.date).left;
        let idx = bisect(chartData, dateAtMouse);
        if (idx > 0 && idx < n) {
          const d0 = chartData[idx - 1].date;
          const d1 = chartData[idx].date;
          idx = (dateAtMouse - d0) > (d1 - dateAtMouse) ? idx : idx - 1;
        }
        idx = Math.max(0, Math.min(n - 1, idx));

        const cx = MARGIN.left + x(chartData[idx].date);
        const d = chartData[idx];

        // Update crosshair lines
        crosshairTop.attr('x1', cx).attr('x2', cx).style('display', null);
        crosshairBottom.attr('x1', cx).attr('x2', cx).style('display', null);

        // Update highlight dots
        if (d.observed != null) {
          highlightObserved
            .attr('cx', cx)
            .attr('cy', MARGIN.top + yTop(d.observed))
            .style('display', null);
        } else {
          highlightObserved.style('display', 'none');
        }

        if (d.signal != null) {
          highlightSignal
            .attr('cx', cx)
            .attr('cy', MARGIN.top + yTop(d.signal))
            .style('display', null);
        } else {
          highlightSignal.style('display', 'none');
        }

        if (d.snr != null) {
          highlightSNR
            .attr('cx', cx)
            .attr('cy', bottomTopY + yBottom(d.snr))
            .style('display', null);
        } else {
          highlightSNR.style('display', 'none');
        }

        setTooltip({
          x: event.clientX + 12,
          y: event.clientY - 10,
          date: d.dateStr,
          observed: d.observed,
          signal: d.signal,
          noise: d.noise,
          snr: d.snr,
        });
      })
      .on('mouseleave', () => {
        crosshairTop.style('display', 'none');
        crosshairBottom.style('display', 'none');
        highlightObserved.style('display', 'none');
        highlightSignal.style('display', 'none');
        highlightSNR.style('display', 'none');
        setTooltip(null);
      });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [chartData]);

  // Empty state
  if (!data || !data.dates || data.dates.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.headerText}>
            <div style={styles.title}>Price Signal vs Noise</div>
            <div style={styles.subtitle}>
              Separating meaningful trends from random fluctuations
            </div>
          </div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No data available
        </div>
      </div>
    );
  }

  const strengthKey = data.signalStrength || 'moderate';
  const strengthCfg = STRENGTH_CONFIG[strengthKey] || STRENGTH_CONFIG.moderate;

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.headerText}>
          <div style={styles.title}>
            Price Signal vs Noise
            {data.field ? ` — ${data.field}` : ''}
          </div>
          <div style={styles.subtitle}>
            Separating meaningful trends from random fluctuations
          </div>
        </div>
        <div style={styles.statsRow}>
          <div style={styles.statBox}>
            <div style={styles.statLabel}>Overall SNR</div>
            <div style={styles.statValue}>
              {data.overallSNR != null ? data.overallSNR.toFixed(1) : 'N/A'}
            </div>
          </div>
          <div style={styles.statBox}>
            <div style={styles.statLabel}>Noise Std Dev</div>
            <div style={styles.statValue}>
              {data.noiseStd != null ? `$${data.noiseStd.toFixed(2)}` : 'N/A'}
            </div>
          </div>
          <span
            style={{
              ...styles.badge,
              background: strengthCfg.background,
              color: strengthCfg.color,
              border: `1px solid ${strengthCfg.border}`,
            }}
          >
            {strengthCfg.label}
          </span>
        </div>
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <svg width="16" height="10">
            <circle cx="8" cy="5" r="3.5" fill="#fff" stroke={OBSERVED_COLOR} strokeWidth="1.5" />
          </svg>
          <span>Observed</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke={SIGNAL_COLOR} strokeWidth="3" />
          </svg>
          <span>Signal (trend)</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="16" height="10">
            <line x1="8" y1="1" x2="8" y2="9" stroke={NOISE_COLOR} strokeWidth="2" strokeOpacity="0.6" />
          </svg>
          <span>Noise</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke={SNR_LINE_COLOR} strokeWidth="2" />
          </svg>
          <span>SNR</span>
        </div>
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {formatDateFull(tooltip.date)}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Observed</span>
            <span style={{ fontWeight: 600, color: OBSERVED_COLOR }}>
              {tooltip.observed != null ? `$${tooltip.observed.toFixed(2)}` : 'N/A'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Signal</span>
            <span style={{ fontWeight: 600, color: SIGNAL_COLOR }}>
              {tooltip.signal != null ? `$${tooltip.signal.toFixed(2)}` : 'N/A'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Noise</span>
            <span style={{ fontWeight: 600, color: NOISE_COLOR }}>
              {tooltip.noise != null
                ? `${tooltip.noise >= 0 ? '+' : ''}$${tooltip.noise.toFixed(2)}`
                : 'N/A'}
            </span>
          </div>
          <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '4px', paddingTop: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span style={{ color: SNR_LINE_COLOR }}>SNR</span>
              <span
                style={{
                  fontWeight: 600,
                  color: tooltip.snr != null
                    ? (tooltip.snr >= 10 ? '#16a34a' : tooltip.snr >= 5 ? '#ca8a04' : '#dc2626')
                    : '#6b7280',
                }}
              >
                {tooltip.snr != null ? tooltip.snr.toFixed(1) : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
